import React from 'react';
import StatusBadge from './UI/StatusBadge';
import { Lock, CheckCircle2, History, ArrowRight } from 'lucide-react';
import { LINK_TEXT } from '../utils/clickable';
import { toCurrency } from '../utils/adminMetrics';
import { cardClass, cardTone } from './UI/dashboardCard';

// Cards that take the place of Apply / Next Payment / Application Status on
// the client dashboard when those have nothing to offer.
//
// The figures and rules come from the API: GET /api/loans/my-summary (totals,
// the current loan, the application in process) and each loan's state.


// A loan code that opens the loan when the card is given onView.
const LoanCode = ({ loan, onView, className = '' }) => {
  const label = loan.loan_code || `Loan #${loan.id}`;
  if (typeof onView !== 'function') return <span className={className}>{label}</span>;
  return (
    <button type="button" onClick={() => onView(loan)} title="Open the loan" className={`${className} ${LINK_TEXT}`}>
      {label}
    </button>
  );
};

const Shell = ({ Icon, color, title, children }) => (
  <div className={`${cardClass(color)} p-4 pt-5 flex flex-col gap-2 min-w-0`}>
    <div className="flex items-center gap-2">
      <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${cardTone(color).icon}`}>
        <Icon size={17} />
      </span>
      <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">{title}</h2>
    </div>
    {children}
  </div>
);

const Stat = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
    <div className="text-sm font-semibold text-gray-900 dark:text-slate-100 truncate">{value}</div>
  </div>
);

// In place of "Need a loan?": the loan that stops a new application.
// current: my-summary's current_loan (what is repayable, paid and owed).
export const CurrentLoanCard = ({ loan, current = null, onView }) => {
  const inProcess = loan.state?.in_process === true;
  const figures = current && current.id === loan.id ? current : null;
  const total = Number(figures?.total_repayable) || 0;
  const paid = figures ? Number(figures.total_paid) || 0 : Number(loan.state?.total_paid) || 0;
  const due = figures ? figures.total_due : loan.total_due;
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  return (
    <Shell Icon={Lock} color="slate" title="Your current loan">
      <div className="flex items-center justify-between gap-2 text-sm">
        <LoanCode loan={loan} onView={onView} className="font-semibold" />
        <StatusBadge status={loan.status} size="small" />
      </div>
      {!inProcess && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Paid" value={toCurrency(paid)} />
            <Stat label="Still to pay" value={toCurrency(due)} />
          </div>
          <div className="h-1.5 rounded-full bg-gray-100 dark:bg-slate-700 overflow-hidden" title={`${pct}% paid`}>
            <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
          </div>
        </>
      )}
      <p className="text-xs text-gray-500 dark:text-slate-400">
        {inProcess
          ? 'You can apply for another loan once this application is finished.'
          : 'You can apply for a new loan once this loan is paid off.'}
      </p>
      {typeof onView === 'function' && (
        <button type="button" onClick={() => onView(loan)} className="self-start inline-flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-300 hover:underline">
          View loan <ArrowRight size={14} />
        </button>
      )}
    </Shell>
  );
};

// In place of "Next Payment": why nothing is due.
export const NoPaymentCard = ({ summary = null, onView }) => {
  const pending = summary?.in_process_loan || null;
  const paidOff = Number(summary?.totals?.paid_off) || 0;
  const repaid = Number(summary?.totals?.repaid) || 0;
  return (
    <Shell Icon={CheckCircle2} color="emerald" title="No payment due">
      {pending ? (
        <p className="text-sm text-gray-600 dark:text-slate-300">
          Payments start once <LoanCode loan={pending} onView={onView} className="font-semibold" /> is approved.
        </p>
      ) : paidOff > 0 ? (
        <>
          <p className="text-sm text-gray-600 dark:text-slate-300">You're all paid up.</p>
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Loans paid off" value={paidOff} />
            <Stat label="Total repaid" value={toCurrency(repaid)} />
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-600 dark:text-slate-300">You have nothing to pay.</p>
      )}
    </Shell>
  );
};

// In place of "Application Status": the client's loans at a glance.
export const LoanSummaryCard = ({ loans = [], summary = null, onView }) => {
  const latest = [...loans].sort((a, b) => (b.id || 0) - (a.id || 0))[0];
  return (
    <Shell Icon={History} color="indigo" title="Your loans">
      {loans.length === 0 ? (
        <p className="text-sm text-gray-600 dark:text-slate-300">No loans yet. Your applications will show here.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Loans" value={Number(summary?.totals?.loans) || 0} />
            <Stat label="Paid off" value={Number(summary?.totals?.paid_off) || 0} />
            <Stat label="Borrowed" value={toCurrency(summary?.totals?.borrowed)} />
          </div>
          {latest && (
            <p className="text-xs text-gray-500 dark:text-slate-400">
              Latest: <LoanCode loan={latest} onView={onView} className="font-medium" /> · {latest.status}
            </p>
          )}
        </>
      )}
    </Shell>
  );
};
